import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  assetTypeApi,
  assetApi,
  assignmentApi,
  assetDocumentApi,
} from "../assetApi";
import useInvalidatingMutation from "./useInvalidatingMutation";

/* ---------- Query keys (as agreed in the asset spec) ---------- */
export const assetKeys = {
  list: (filters) => ["assets", filters],
  detail: (id) => ["asset", id],
  history: (id) => ["asset", id, "history"],
  assignments: (filters) => ["assignments", filters],
  assignment: (id) => ["assignment", id],
  clearance: (employeeId) => ["clearance", employeeId],
  types: (includeInactive) => ["assetTypes", includeInactive],
  documents: (id) => ["asset", id, "documents"],
};

// After any assign / return / transfer / lost / status / branch / delete
const CUSTODY_KEYS = [
  ["assets"],
  ["asset"],
  ["assignments"],
  ["assignment"],
  ["clearance"],
];
const TYPE_KEYS = [["assetTypes"]];

/* ---------- Asset types ---------- */
export const useAssetTypes = (includeInactive = false) =>
  useQuery({
    queryKey: assetKeys.types(includeInactive),
    queryFn: () => assetTypeApi.getAll(includeInactive),
    staleTime: 5 * 60 * 1000,
  });

export const useCreateAssetType = () =>
  useInvalidatingMutation((body) => assetTypeApi.create(body), TYPE_KEYS);
export const useUpdateAssetType = () =>
  useInvalidatingMutation(
    ({ id, body }) => assetTypeApi.update(id, body),
    [...TYPE_KEYS, ["assets"]],
  );
export const useDeleteAssetType = () =>
  useInvalidatingMutation((id) => assetTypeApi.remove(id), TYPE_KEYS);

/* ---------- Assets ---------- */
export const useAssets = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: assetKeys.list(filters),
    queryFn: () => assetApi.getList(filters),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useAsset = (id) =>
  useQuery({
    queryKey: assetKeys.detail(id),
    queryFn: () => assetApi.getById(id),
    enabled: !!id,
  });

export const useAssetHistory = (id) =>
  useQuery({
    queryKey: assetKeys.history(id),
    queryFn: () => assetApi.getHistory(id),
    enabled: !!id,
  });

export const useCreateAsset = () =>
  useInvalidatingMutation((body) => assetApi.create(body), CUSTODY_KEYS);
export const useUpdateAsset = () =>
  useInvalidatingMutation(
    ({ id, body }) => assetApi.update(id, body),
    CUSTODY_KEYS,
  );
export const useSetAssetStatus = () =>
  useInvalidatingMutation(
    ({ id, status, remarks }) => assetApi.setStatus(id, { status, remarks }),
    CUSTODY_KEYS,
  );
export const useMoveAssetBranch = () =>
  useInvalidatingMutation(
    ({ id, branchId }) => assetApi.moveBranch(id, branchId),
    CUSTODY_KEYS,
  );
export const useDeleteAsset = () =>
  useInvalidatingMutation((id) => assetApi.remove(id), CUSTODY_KEYS);

/* ---------- Assignments ---------- */
export const useAssignments = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: assetKeys.assignments(filters),
    queryFn: () => assignmentApi.getList(filters),
    placeholderData: keepPreviousData,
    enabled,
  });

/** Total for a tab badge (limit=1, read `total`). */
export const useAssignmentCount = (filters) =>
  useQuery({
    queryKey: assetKeys.assignments({ ...filters, page: 1, limit: 1 }),
    queryFn: () => assignmentApi.getList({ ...filters, page: 1, limit: 1 }),
    select: (res) => res?.total ?? 0,
  });

export const useClearance = (employeeId, { enabled = true } = {}) =>
  useQuery({
    queryKey: assetKeys.clearance(employeeId),
    queryFn: () => assignmentApi.getClearance(employeeId),
    enabled: !!employeeId && enabled,
    retry: false, // 403 = not allowed to view this employee
  });

export const useAssignAsset = () =>
  useInvalidatingMutation((args) => assignmentApi.assign(args), CUSTODY_KEYS);
export const useSetDateReceived = () =>
  useInvalidatingMutation(
    ({ id, dateReceived }) => assignmentApi.setDateReceived(id, dateReceived),
    CUSTODY_KEYS,
  );
export const useReturnAsset = () =>
  useInvalidatingMutation(
    ({ id, ...body }) => assignmentApi.returnAsset(id, body),
    CUSTODY_KEYS,
  );
export const useTransferAsset = () =>
  useInvalidatingMutation(
    ({ id, ...body }) => assignmentApi.transfer(id, body),
    CUSTODY_KEYS,
  );
export const useMarkAssetLost = () =>
  useInvalidatingMutation(
    ({ id, notes }) => assignmentApi.markLost(id, notes),
    CUSTODY_KEYS,
  );

/* ---------- Asset documents ---------- */
export const useAssetDocuments = (assetId) =>
  useQuery({
    queryKey: assetKeys.documents(assetId),
    queryFn: () => assetDocumentApi.list(assetId),
    enabled: !!assetId,
  });
export const useUploadAssetDocument = () =>
  useInvalidatingMutation(
    ({ assetId, ...body }) => assetDocumentApi.upload(assetId, body),
    [["asset"]],
  );
export const useDeleteAssetDocument = () =>
  useInvalidatingMutation(
    ({ assetId, documentId }) => assetDocumentApi.remove(assetId, documentId),
    [["asset"]],
  );
